import type { TracePoint } from '@rw/game-engine';
import type { FinishRunResponse } from '@rw/shared';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export type LocalRunStatus = 'active' | 'paused' | 'finished' | 'synced' | 'failed';

export interface LocalRun {
  id: string;
  userId: string;
  regionId: string;
  startedAt: number;
  endedAt: number | null;
  status: LocalRunStatus;
  /** Current tracking segment (incremented on each resume). */
  segment: number;
  /** Next point sequence number. */
  nextSeq: number;
  clientDistanceM: number;
  /** Active (non-paused) time accumulated before the current running span. */
  activeMs: number;
  /** Clock time when the current running span began; null while paused. */
  runningSince: number | null;
  result: FinishRunResponse | null;
  error: string | null;
  updatedAt: number;
}

/** Sync bookkeeping, owned exclusively by the SyncManager (the engine never writes it). */
export interface SyncState {
  runId: string;
  serverCreated: boolean;
  /** Highest seq confirmed by the server (-1 = none). */
  uploadedSeq: number;
}

export const initialSyncState = (runId: string): SyncState => ({ runId, serverCreated: false, uploadedSeq: -1 });

export interface StoredPoint extends TracePoint {
  runId: string;
}

/** Durable local persistence for runs. Every GPS fix is written before anything else happens. */
export interface RunStore {
  readonly durable: boolean;
  putRun(run: LocalRun): Promise<void>;
  getRun(id: string): Promise<LocalRun | undefined>;
  listRuns(): Promise<LocalRun[]>;
  deleteRun(id: string): Promise<void>;
  /** Atomically appends a point and updates the run. */
  appendPoint(run: LocalRun, point: StoredPoint): Promise<void>;
  getPoints(runId: string, afterSeq?: number): Promise<StoredPoint[]>;
  countPoints(runId: string): Promise<number>;
  deletePoints(runId: string): Promise<void>;
  getSync(runId: string): Promise<SyncState>;
  putSync(state: SyncState): Promise<void>;
}

interface RwDb extends DBSchema {
  runs: { key: string; value: LocalRun; indexes: { byStatus: string } };
  points: { key: [string, number]; value: StoredPoint; indexes: { byRun: string } };
  sync: { key: string; value: SyncState };
}

export class IdbRunStore implements RunStore {
  readonly durable = true;
  private constructor(private readonly db: IDBPDatabase<RwDb>) {}

  static async open(name = 'runnerwars'): Promise<IdbRunStore> {
    const db = await openDB<RwDb>(name, 1, {
      upgrade(db) {
        const runs = db.createObjectStore('runs', { keyPath: 'id' });
        runs.createIndex('byStatus', 'status');
        const points = db.createObjectStore('points', { keyPath: ['runId', 'seq'] });
        points.createIndex('byRun', 'runId');
        db.createObjectStore('sync', { keyPath: 'runId' });
      },
    });
    return new IdbRunStore(db);
  }

  async putRun(run: LocalRun) {
    await this.db.put('runs', { ...run, updatedAt: Date.now() });
  }

  getRun(id: string) {
    return this.db.get('runs', id);
  }

  listRuns() {
    return this.db.getAll('runs');
  }

  async deleteRun(id: string) {
    await this.deletePoints(id);
    await this.db.delete('sync', id);
    await this.db.delete('runs', id);
  }

  async getSync(runId: string) {
    return (await this.db.get('sync', runId)) ?? initialSyncState(runId);
  }

  async putSync(state: SyncState) {
    await this.db.put('sync', state);
  }

  async appendPoint(run: LocalRun, point: StoredPoint) {
    const tx = this.db.transaction(['runs', 'points'], 'readwrite');
    await Promise.all([
      tx.objectStore('points').put(point),
      tx.objectStore('runs').put({ ...run, updatedAt: Date.now() }),
      tx.done,
    ]);
  }

  async getPoints(runId: string, afterSeq = -1) {
    const range = IDBKeyRange.bound([runId, afterSeq + 1], [runId, Number.MAX_SAFE_INTEGER]);
    return this.db.getAll('points', range);
  }

  countPoints(runId: string) {
    return this.db.countFromIndex('points', 'byRun', runId);
  }

  async deletePoints(runId: string) {
    const range = IDBKeyRange.bound([runId, 0], [runId, Number.MAX_SAFE_INTEGER]);
    await this.db.delete('points', range);
  }
}

/** Fallback when IndexedDB is unavailable (e.g. some private modes). Data survives only while the tab lives. */
export class MemoryRunStore implements RunStore {
  readonly durable = false;
  private runs = new Map<string, LocalRun>();
  private points = new Map<string, StoredPoint[]>();
  private sync = new Map<string, SyncState>();

  async getSync(runId: string) {
    return this.sync.get(runId) ?? initialSyncState(runId);
  }
  async putSync(state: SyncState) {
    this.sync.set(state.runId, { ...state });
  }

  async putRun(run: LocalRun) {
    this.runs.set(run.id, { ...run, updatedAt: Date.now() });
  }
  async getRun(id: string) {
    return this.runs.get(id);
  }
  async listRuns() {
    return [...this.runs.values()];
  }
  async deleteRun(id: string) {
    this.runs.delete(id);
    this.points.delete(id);
    this.sync.delete(id);
  }
  async appendPoint(run: LocalRun, point: StoredPoint) {
    const list = this.points.get(run.id) ?? [];
    if (!list.some((p) => p.seq === point.seq)) list.push(point);
    this.points.set(run.id, list);
    this.runs.set(run.id, { ...run, updatedAt: Date.now() });
  }
  async getPoints(runId: string, afterSeq = -1) {
    return (this.points.get(runId) ?? []).filter((p) => p.seq > afterSeq).sort((a, b) => a.seq - b.seq);
  }
  async countPoints(runId: string) {
    return this.points.get(runId)?.length ?? 0;
  }
  async deletePoints(runId: string) {
    this.points.delete(runId);
  }
}

let storePromise: Promise<RunStore> | null = null;

export function getRunStore(): Promise<RunStore> {
  storePromise ??= IdbRunStore.open().catch((e) => {
    console.warn('IndexedDB unavailable, falling back to memory store', e);
    return new MemoryRunStore();
  });
  return storePromise;
}

export function setRunStoreForTests(store: RunStore | null) {
  storePromise = store ? Promise.resolve(store) : null;
}
